import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { PublicCourse, PublicClassroom } from '@/types/course';
import { Registration, PaymentInfo } from '@/services/registrationApi';
import { formatPriceSimple } from '@/utils/courseFormat';
import { formatDbDateVi } from '@/utils/dbDateDisplay';
import {
  CheckCircleIcon,
  ClipboardDocumentIcon,
  CalendarDaysIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface PaymentQRDisplayProps {
  registration: Registration;
  paymentInfo: PaymentInfo;
  course: PublicCourse;
  classroom: PublicClassroom;
  onClose: () => void;
}

const PaymentQRDisplay: React.FC<PaymentQRDisplayProps> = ({
  registration,
  paymentInfo,
  course,
  classroom,
  onClose
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  const qrSource = paymentInfo.qrCode || '';

  // Check if qrCode is a URL (SePay) or a QR string
  const isQrUrl = qrSource.startsWith('http://') || qrSource.startsWith('https://');

  // Generate QR code from string if needed (backward compatibility)
  useEffect(() => {
    // If it's already a URL, use it directly
    if (isQrUrl) {
      setQrDataUrl(qrSource);
      return;
    }

    // Otherwise, generate QR code from string (backward compatibility)
    const generateQRCode = async () => {
      if (!qrSource) {
        setQrDataUrl('');
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(qrSource, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrDataUrl(dataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [qrSource, isQrUrl]);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const formatDeadline = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffInHours = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours <= 0) {
      return 'Đã hết hạn';
    } else if (diffInHours <= 24) {
      return `${diffInHours} giờ`;
    } else {
      const days = Math.floor(diffInHours / 24);
      const hours = diffInHours % 24;
      return `${days} ngày ${hours > 0 ? hours + ' giờ' : ''}`.trim();
    }
  };


  return (
    <div className="max-w-2xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-m2-secondary/15 rounded-full mb-4">
          <CheckCircleIcon className="h-8 w-8 text-m2-secondary" />
        </div>
        <h3 className="text-xl font-semibold text-m2-on-surface mb-2">
          Đăng ký thành công!
        </h3>
        <p className="text-m2-on-surface-variant">
          Mã đăng ký: <span className="font-mono font-medium text-m2-primary">{registration.code}</span>
        </p>
      </div>

      {/* Payment Instructions */}
      <div className="bg-m2-primary/10 border border-m2-primary/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-m2-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-m2-on-surface mb-1">Hướng dẫn thanh toán</h4>
            <p className="text-m2-primary text-sm">
              Vui lòng thanh toán trong vòng <strong>{getTimeRemaining(paymentInfo.deadline)}</strong> để
              hoàn tất đăng ký. Sau khi thanh toán, ban tổ chức sẽ xác nhận và gửi thông tin chi tiết qua email.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Code */}
        <div className="text-center">
          <h4 className="font-medium text-m2-on-surface mb-4">Quét mã QR để thanh toán</h4>
          <div className="bg-white border-2 border-m2-outline-variant rounded-lg p-4 inline-block">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="VietQR Code"
                className="w-48 h-48 mx-auto"
              />
            ) : (
              <div className="w-48 h-48 mx-auto bg-gray-100 flex items-center justify-center">
                <span className="text-gray-500">Đang tạo mã QR...</span>
              </div>
            )}
          </div>
          <p className="text-xs text-m2-on-surface-variant mt-2">
            Quét mã bằng app ngân hàng hoặc ví điện tử
          </p>
        </div>

        {/* Payment Details */}
        <div className="space-y-4">
          <h4 className="font-medium text-m2-on-surface">Thông tin chuyển khoản</h4>
          
          {/* Bank Info */}
          <div className="bg-m2-surface-low rounded-lg p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-m2-on-surface-variant">Ngân hàng:</span>
                <span className="font-medium">{paymentInfo.bankName ?? ''}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-m2-on-surface-variant">Số tài khoản:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-medium">{paymentInfo.bankAccount ?? ''}</span>
                  <button
                    onClick={() => copyToClipboard(paymentInfo.bankAccount ?? '', 'account')}
                    className="text-m2-primary hover:text-m2-primary"
                    title="Sao chép số tài khoản"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                  {copiedField === 'account' && (
                    <span className="text-xs text-m2-secondary">Đã sao chép!</span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-m2-on-surface-variant">Số tiền:</span>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-lg text-m2-primary">
                    {formatPriceSimple(paymentInfo.amount ?? 0)}
                  </span>
                  <button
                    onClick={() => copyToClipboard(String(paymentInfo.amount ?? 0), 'amount')}
                    className="text-m2-primary hover:text-m2-primary"
                    title="Sao chép số tiền"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                  {copiedField === 'amount' && (
                    <span className="text-xs text-m2-secondary">Đã sao chép!</span>
                  )}
                </div>
              </div>
              
              <div className="flex items-start justify-between">
                <span className="text-sm text-m2-on-surface-variant">Nội dung CK:</span>
                <div className="flex items-center gap-2 text-right">
                  <span className="font-mono font-medium text-red-600">
                    {paymentInfo.transferContent ?? ''}
                  </span>
                  <button
                    onClick={() => copyToClipboard(paymentInfo.transferContent ?? '', 'content')}
                    className="text-m2-primary hover:text-m2-primary"
                    title="Sao chép nội dung chuyển khoản"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                  {copiedField === 'content' && (
                    <span className="text-xs text-m2-secondary">Đã sao chép!</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Deadline */}
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-center gap-2 text-amber-900">
              <CalendarDaysIcon className="h-5 w-5 flex-shrink-0" />
              <div>
                <div className="font-semibold">Hạn thanh toán</div>
                <div className="text-sm">{formatDeadline(paymentInfo.deadline)}</div>
                <div className="text-sm">
                  Còn lại: <span className="font-semibold">{getTimeRemaining(paymentInfo.deadline)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-red-50 border border-red-300 rounded-lg p-4">
            <div className="text-red-900 text-sm">
              <div className="font-semibold mb-1">⚠️ Lưu ý quan trọng:</div>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Vui lòng chuyển khoản <strong>đúng số tiền</strong> và <strong>đúng nội dung</strong></li>
                <li>Nội dung chuyển khoản là mã đăng ký, không thay đổi</li>
                <li>Sau khi thanh toán, đăng ký sẽ được xác nhận trong 24h</li>
                <li>Liên hệ hỗ trợ nếu có vấn đề</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Course Summary */}
      <div className="mt-6 pt-6 border-t">
        <h4 className="font-medium text-m2-on-surface mb-3">Tóm tắt đăng ký</h4>
        <div className="bg-m2-surface-low rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-m2-on-surface-variant">Khóa học:</span>
              <span className="ml-2 font-medium">{course.name}</span>
            </div>
            <div>
              <span className="text-m2-on-surface-variant">Lớp học:</span>
              <span className="ml-2 font-medium">{classroom.name}</span>
            </div>
            <div>
              <span className="text-m2-on-surface-variant">Mã lớp:</span>
              <span className="ml-2 font-mono font-medium">{classroom.code}</span>
            </div>
            <div>
              <span className="text-m2-on-surface-variant">Mã đăng ký:</span>
              <span className="ml-2 font-mono font-medium text-m2-primary">{registration.code}</span>
            </div>
            {classroom.startDate && (
              <div className="sm:col-span-2">
                <span className="text-m2-on-surface-variant">Ngày bắt đầu:</span>
                <span className="ml-2 font-medium">
                  {formatDbDateVi(classroom.startDate)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-center mt-6 pt-6 border-t">
        <button
          onClick={onClose}
          className="px-6 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow"
        >
          Đóng
        </button>
      </div>
    </div>
  );
};

export default PaymentQRDisplay;